
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { AdyenPlatformExperience, PayoutsOverview, TransactionsOverview } from '@adyen/adyen-platform-experience-web';
// Webpack 4 ignores package `exports` maps, so the stylesheet is imported by its
// real path rather than through the package's `./…css` subpath export.
import '@adyen/adyen-platform-experience-web/dist/adyen-platform-experience-web.css';
import api from '@/api/http/SmoothrPayApiService';
import { AdyenPlatformComponent } from '@/enums/SmoothrPay';

/** Adyen accepts `en-US`-style locales; ours are bare language codes. */
const ADYEN_LOCALES: { [key: string]: string } = {
  en: 'en-US',
  de: 'de-DE',
  fr: 'fr-FR',
  it: 'it-IT',
  nl: 'nl-NL',
};

/**
 * Embeds one Adyen "platform experience" overview — transactions or payouts.
 *
 * The SDK owns the DOM inside `mountEl` and takes its locale at construction,
 * so a language switch or a different merchant means tearing it down and
 * building it again.
 */
@Component
export default class AdyenPlatformSection extends Vue {
  @Prop({ type: String, required: true }) public configurationId!: string;
  /** Present once the account-holder provisioning step has run. */
  @Prop({ type: String, default: '' }) public accountHolderId!: string;
  /** Which single Adyen overview to render — one per navigation item. */
  @Prop({ type: String, required: true }) public component!: AdyenPlatformComponent;

  public $refs!: { mountEl: HTMLElement };

  public ready: boolean = false;
  public error: string = '';

  private overviews: Array<{ unmount: () => any }> = [];

  public mounted() {
    this.setup();
  }

  public beforeDestroy() {
    this.unmountAll();
  }

  // Re-initialise when the merchant changes, once the account holder exists, or
  // when the user switches language.
  @Watch('configurationId')
  @Watch('accountHolderId')
  @Watch('component')
  public onInputChange() {
    this.setup();
  }

  @Watch('$i18n.locale')
  public onLocaleChange() {
    this.setup();
  }

  public async setup() {
    if (!this.accountHolderId) {
      return;
    }
    this.error = '';
    this.ready = false;
    this.unmountAll();

    try {
      const core = await AdyenPlatformExperience({
        environment: (process.env.VUE_APP_ADYEN_PLATFORM_ENVIRONMENT as 'test' | 'live') || 'test',
        locale: (ADYEN_LOCALES[this.$i18n.locale] || 'en-US') as any,
        onSessionCreate: this.requestSession,
        onError: (e: Error) => {
          this.error = e.message;
        },
      });

      const Overview = this.overview;
      if (this.$refs.mountEl && Overview) {
        this.overviews.push(new Overview({ core }).mount(this.$refs.mountEl));
      }
      this.ready = true;
    } catch (e) {
      this.error = e instanceof Error ? e.message : (this.$t('smoothrPay.adyen.setupFailed') as string);
    }
  }

  /**
   * Adyen asks for a session whenever it needs to (re)authenticate, so this
   * fetches a fresh token each time rather than closing over one.
   */
  public async requestSession(): Promise<{ id: string; token: string }> {
    // Only the component actually shown: the service derives the session's
    // Adyen roles from what is requested, so asking for one narrows the token.
    const { data } = await api.createComponentSession(this.configurationId, [this.component]);
    return { id: data.id, token: data.token };
  }

  private get overview(): any {
    return this.component === AdyenPlatformComponent.PAYOUTS_OVERVIEW ? PayoutsOverview : TransactionsOverview;
  }

  private unmountAll() {
    for (const overview of this.overviews) {
      try {
        overview.unmount();
      } catch (e) {
        // already gone
      }
    }
    this.overviews = [];
  }
}
